import React from "react";
import { motion } from "framer-motion";
import {
  FaBrain,
  FaChartLine,
  FaRocket,
  FaShieldAlt,
  FaStar,
  FaLightbulb,
  FaDatabase,
  FaInfinity,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const AIJathagamSection = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const t = {
    English: {
      title: "AI-Powered Vedic Astrology",
      subtitle: "Ancient Wisdom, Modern Precision",
      description:
        "Experience the future of astrological analysis with our advanced AI technology that combines centuries-old Vedic knowledge with cutting-edge artificial intelligence for unparalleled insights.",
      ctaPrimary: "Begin Your Journey",
      ctaSecondary: "Learn More",
      features: [
        {
          icon: <FaBrain />,
          title: "Intelligent Analysis",
          description:
            "Deep learning algorithms analyze 10,000+ astrological parameters",
        },
        {
          icon: <FaChartLine />,
          title: "Predictive Accuracy",
          description: "94% accurate predictions verified by our expert panel",
        },
        {
          icon: <FaShieldAlt />,
          title: "Complete Privacy",
          description:
            "End-to-end encryption ensures your data remains confidential",
        },
        {
          icon: <FaRocket />,
          title: "Instant Results",
          description: "Comprehensive analysis delivered in under 30 seconds",
        },
      ],
      stats: [
        { number: "50,000+", label: "Analyses Completed" },
        { number: "98.2%", label: "User Satisfaction" },
        { number: "24/7", label: "AI Availability" },
        { number: "10,000+", label: "Planetary Combinations" },
      ],
      highlight:
        "The most advanced AI astrology platform, trusted by thousands worldwide.",
    },
    Tamil: {
      title: "AI இயக்கப்படும் வைதிக ஜோதிடம்",
      subtitle: "பண்டைய ஞானம், நவீன துல்லியம்",
      description:
        "நூற்றாண்டுகள் பழமையான வைதிக அறிவை முன்னணி செயற்கை நுண்ணறிவுடன் இணைக்கும் எங்கள் மேம்பட்ட AI தொழில்நுட்பத்துடன் ஜோதிட பகுப்பாய்வின் எதிர்காலத்தை அனுபவிக்கவும்.",
      ctaPrimary: "உங்கள் பயணத்தை தொடங்குங்கள்",
      ctaSecondary: "மேலும் அறிக",
      features: [
        {
          icon: <FaBrain />,
          title: "நுண்ணறிவு பகுப்பாய்வு",
          description:
            "10,000+ ஜோதிட அளவுருக்களை ஆழமான கற்றல் அல்காரிதங்கள் பகுப்பாய்வு செய்கின்றன",
        },
        {
          icon: <FaChartLine />,
          title: "முன்கணிப்பு துல்லியம்",
          description:
            "எங்கள் நிபுணர் குழுவால் சரிபார்க்கப்பட்ட 94% துல்லியமான கணிப்புகள்",
        },
        {
          icon: <FaShieldAlt />,
          title: "முழுமையான தனியுரிமை",
          description:
            "முழு-மறையாக்கம் உங்கள் தரவு இரகசியமாக இருக்கும் என்பதை உறுதி செய்கிறது",
        },
        {
          icon: <FaRocket />,
          title: "உடனடி முடிவுகள்",
          description: "30 வினாடிகளுக்குள் வழங்கப்படும் விரிவான பகுப்பாய்வு",
        },
      ],
      stats: [
        { number: "50,000+", label: "பகுப்பாய்வுகள் முடிக்கப்பட்டன" },
        { number: "98.2%", label: "பயனர் திருப்தி" },
        { number: "24/7", label: "AI கிடைப்பு" },
        { number: "10,000+", label: "கோள சேர்க்கைகள்" },
      ],
      highlight:
        "மிகவும் மேம்பட்ட AI ஜோதிட தளம், உலகம் முழுவதும் ஆயிரக்கணக்கானோரால் நம்பப்படுகிறது.",
    },
  };

  const txt = t[language];

  return (
    <section className="relative py-24 px-4 md:px-8 bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-hidden font-['Inter',sans-serif]">
      {/* Subtle Background Orbs - Rose Theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-rose-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-rose-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          {/* Badge */}
          <motion.div
            initial={{ scale: 0.8 }}
            whileInView={{ scale: 1 }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-rose-100 text-rose-700 font-medium text-sm uppercase tracking-wider mb-8"
          >
            <FaStar className="animate-pulse" />
            {language === "English" ? "AI Revolution" : "AI புரட்சி"}
          </motion.div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-tight">
            {txt.title}
          </h1>
          <p className="text-2xl md:text-4xl font-light text-rose-600 mb-8">
            {txt.subtitle}
          </p>

          {/* Description */}
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto mb-12 leading-relaxed">
            {txt.description}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 20px 40px rgba(244, 114, 182, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate("/login")}
              className="px-10 py-5 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold rounded-2xl shadow-xl flex items-center justify-center gap-3 group"
            >
              {txt.ctaPrimary}
              <FaRocket className="group-hover:translate-x-2 transition-transform" />
            </motion.button>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-24">
          {txt.features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -12, scale: 1.03 }}
              className="bg-white rounded-3xl p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-rose-100"
            >
              <div className="inline-flex p-5 rounded-2xl bg-gradient-to-br from-rose-500 to-pink-500 text-white text-3xl mb-6">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mt-24">
          {/* Visual Orbit Animation - Rose Theme */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative flex justify-center"
          >
            <div className="relative w-80 h-80 md:w-96 md:h-96">
              {/* Outer Orbit Ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 border-4 border-rose-200 rounded-full opacity-40"
              />

              {/* Middle Orbit Ring */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                className="absolute inset-8 border-4 border-pink-200 rounded-full opacity-30"
              />

              {/* Inner Orbit Ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="absolute inset-16 border-4 border-rose-300 rounded-full opacity-50"
              />

              {/* Zodiac Symbols Orbiting ON the Outer Ring */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0"
              >
                {[
                  "♈",
                  "♉",
                  "♊",
                  "♋",
                  "♌",
                  "♍",
                  "♎",
                  "♏",
                  "♐",
                  "♑",
                  "♒",
                  "♓",
                ].map((sign, index) => {
                  const angle = index * 30; // Evenly spaced every 30 degrees
                  const radius = 160; // Matches outer ring (adjust if changing size: for md ~192px)
                  return (
                    <div
                      key={sign}
                      className="absolute text-3xl text-rose-500 drop-shadow-lg"
                      style={{
                        top: "50%",
                        left: "50%",
                        transform: `translate(-50%, -50%) translate(${
                          Math.sin((angle * Math.PI) / 180) * radius
                        }px, ${-Math.cos((angle * Math.PI) / 180) * radius}px)`,
                      }}
                    >
                      <span className="block animate-pulse">{sign}</span>
                    </div>
                  );
                })}
              </motion.div>

              {/* Floating Glowing Stars (Mystical Particles) */}
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <motion.div
                  key={`star-${i}`}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.4, 1, 0.4],
                    scale: [1, 1.3, 1],
                  }}
                  transition={{
                    duration: 4 + i,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.5,
                  }}
                  className="absolute text-pink-300 text-3xl"
                  style={{
                    top: `${20 + i * 15}%`,
                    left: `${15 + i * 12}%`,
                  }}
                >
                  ✦
                </motion.div>
              ))}

              {/* Tiny Orbiting Planets / AI Nodes (on inner paths) */}
              {[0, 1, 2].map((i) => {
                const radius = 80 + i * 30;
                const duration = 15 + i * 5;
                return (
                  <motion.div
                    key={`planet-${i}`}
                    animate={{ rotate: i % 2 === 0 ? 360 : -360 }}
                    transition={{ duration, repeat: Infinity, ease: "linear" }}
                    className="absolute w-4 h-4 bg-gradient-to-br from-rose-400 to-pink-400 rounded-full shadow-lg"
                    style={{
                      top: "50%",
                      left: "50%",
                      x: "-50%",
                      y: "-50%",
                    }}
                  >
                    <div className="absolute inset-0 rounded-full bg-white opacity-30 animate-ping" />
                  </motion.div>
                );
              })}

              {/* Center Infinity Symbol with Gentle Pulse */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="p-8 bg-gradient-to-br from-rose-600 to-pink-600 rounded-full shadow-2xl text-white"
                >
                  <FaInfinity className="text-6xl" />
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-12"
          >
            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className="p-4 rounded-2xl bg-rose-100 text-rose-600">
                  <FaLightbulb className="text-3xl" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">
                    {language === "English"
                      ? "Smart Insights"
                      : "சாமர்த்தியமான நுண்ணறிவுகள்"}
                  </h4>
                  <p className="text-gray-600">
                    {language === "English"
                      ? "AI-driven insights that adapt to your unique celestial patterns"
                      : "உங்கள் தனித்துவமான வானியல் வடிவங்களுக்கு ஏற்ப அமையும் AI-இயக்கப்படும் நுண்ணறிவுகள்"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="p-4 rounded-2xl bg-pink-100 text-pink-600">
                  <FaDatabase className="text-3xl" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">
                    {language === "English"
                      ? "Vast Knowledge Base"
                      : "பெரிய அறிவுத் தளம்"}
                  </h4>
                  <p className="text-gray-600">
                    {language === "English"
                      ? "Access to centuries of Vedic literature combined with modern data science"
                      : "நவீன தரவு அறிவியலுடன் இணைந்த நூற்றாண்டுகால வைதிக இலக்கியங்களுக்கான அணுகல்"}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AIJathagamSection;
